import React, { useRef, useState } from "react";
import { theme } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  PaperclipIcon,
  XIcon,
  DotsSixVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@phosphor-icons/react";
import { getFileExtension } from "pages/media/utils";
import {
  DOCUMENT_ACCEPT,
  formatFileSize,
  extStyle,
  matchesAccept,
  uploadFileKey,
} from "../helpers/fileList";
import { usePlaybookDraft } from "../context/PlaybookDraftContext";
import { OrderNumber } from "./OrderNumber.styles";
import {
  Dropzone,
  DropTitle,
  DropSubtitle,
  FileList,
  FileRowWrap,
  FileRow,
  DragHandle,
  FileInfo,
  FileName,
  FileMeta,
  ExtTag,
  IconBtn,
  VisibilityToggle,
  AddMoreBtn,
  Note,
} from "./ContentSidebar.styles";

/**
 * File drop/browse target + reorderable file list, reading and writing the
 * shared `PlaybookDraftContext` document list. Shared by Step 1's sidebar and
 * the "Create now" blocked modal so both offer the exact same upload
 * behavior on the exact same document state.
 */
export const DocumentUploadArea: React.FC = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const {
    documents,
    hiddenDocKeys,
    addDocuments,
    removeDocument,
    reorderDocuments,
    toggleDocumentHidden,
  } = usePlaybookDraft();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragIndex = useRef<number | null>(null);
  const [isDropActive, setIsDropActive] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDropzoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropActive(false);
    // The file picker enforces `accept`, but drag-drop bypasses it — validate
    // dropped files against the same allowed types so both paths agree.
    const accepted = Array.from(e.dataTransfer.files).filter((file) =>
      matchesAccept(file, DOCUMENT_ACCEPT)
    );
    if (accepted.length > 0) addDocuments(accepted);
  };

  const hasFiles = documents.length > 0;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={DOCUMENT_ACCEPT}
        style={{ display: "none" }}
        onChange={(e) => {
          addDocuments(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />

      {hasFiles ? (
        <>
          <FileList>
            {documents.map((f, i) => {
              const ext = extStyle(f.name);
              const key = uploadFileKey(f);
              const hidden = hiddenDocKeys.has(key);
              return (
                <FileRowWrap key={f.uid}>
                  <OrderNumber $token={token} $isMain={i === 0}>
                    {i + 1}
                  </OrderNumber>
                  <FileRow
                    $token={token}
                    $dragOver={dragOverIndex === i}
                    draggable
                    onDragStart={() => {
                      dragIndex.current = i;
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverIndex(i);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIndex.current !== null)
                        reorderDocuments(dragIndex.current, i);
                      dragIndex.current = null;
                      setDragOverIndex(null);
                    }}
                    onDragEnd={() => {
                      dragIndex.current = null;
                      setDragOverIndex(null);
                    }}
                  >
                    <DragHandle $token={token}>
                      <DotsSixVerticalIcon size={16} />
                    </DragHandle>
                    <FileInfo>
                      <FileName $token={token}>{f.name}</FileName>
                      <FileMeta $token={token}>
                        {formatFileSize(f.size)}
                        <ExtTag $color={ext.color} $bg={ext.bg}>
                          {getFileExtension(f.name).toUpperCase()}
                        </ExtTag>
                      </FileMeta>
                    </FileInfo>
                    <VisibilityToggle
                      $token={token}
                      $hidden={hidden}
                      type="button"
                      aria-label={t("createExperience.step1.toggleVisibility")}
                      onClick={() => toggleDocumentHidden(key)}
                    >
                      {hidden ? (
                        <EyeSlashIcon size={15} />
                      ) : (
                        <EyeIcon size={15} />
                      )}
                    </VisibilityToggle>
                    <IconBtn
                      $token={token}
                      type="button"
                      aria-label={t("createExperience.step1.remove")}
                      onClick={() => removeDocument(key)}
                    >
                      <XIcon size={13} />
                    </IconBtn>
                  </FileRow>
                </FileRowWrap>
              );
            })}
          </FileList>

          <AddMoreBtn
            $token={token}
            type="button"
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperclipIcon size={15} />
            {t("createExperience.step1.addMore")}
          </AddMoreBtn>
        </>
      ) : (
        <Dropzone
          $token={token}
          $active={isDropActive}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDropActive(true);
          }}
          onDragLeave={() => setIsDropActive(false)}
          onDrop={handleDropzoneDrop}
        >
          <PaperclipIcon size={40} />
          <DropTitle $token={token}>
            {t("createExperience.step1.dropTitle")}
          </DropTitle>
          <DropSubtitle $token={token}>
            {t("createExperience.step1.dropSubtitle")}
          </DropSubtitle>
        </Dropzone>
      )}

      <Note $token={token}>{t("createExperience.step1.note")}</Note>
    </>
  );
};
